<template>
  <div class="app-container">
    <div class="pcpdaClass">PC</div>
    <el-tree
      ref="treeRefPC"
      :check-strictly="true"
      :data="permissionList.pc"
      :props="props"
      icon-class="el-icon-thumb"
      node-key="id"
      show-checkbox
      @check="handleTreeCheckPC"
    />

    <div class="pcpdaClass" style="margin-top: 20px">PDA</div>
    <el-tree
      ref="treeRefPDA"
      :check-strictly="true"
      :data="permissionList.pda"
      :props="props"
      icon-class="el-icon-thumb"
      node-key="id"
      show-checkbox
      @check="handleTreeCheckPDA"
    />
  </div>
</template>

<script>
import { getPermissionStructureAll } from '@/api/permission';

export default {
  name: 'PermissionTree',
  props: {
    permissionTreePop: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      props: {
        label: 'name',
        children: 'children',
        disabled: 'disabled'
      },
      permissionList: {
        pc: [],
        pda: []
      },
      userId: null
    };
  },
  mounted() {
    this.userId = this.$store.state.user.id;
    this.init();
  },
  methods: {
    /**
     * 递归标记角色权限节点为 disabled（只读）
     * 仅当当前节点及其整棵子树都属于角色权限时，父节点才禁用
     * @param {Array} nodes 权限树节点列表
     * @param {Set} roleIdSet 角色权限 ID 集合
     */
    markRoleNodes(nodes, roleIdSet) {
      if (!nodes || !nodes.length) return nodes;
      return nodes.map(node => {
        const children = node.children && node.children.length > 0
          ? this.markRoleNodes(node.children, roleIdSet)
          : [];
        const isRolePermission = roleIdSet.has(node.id) || roleIdSet.has(String(node.id));
        const hasChildren = children.length > 0;
        const allChildrenDisabled = hasChildren && children.every(child => child.disabled);
        const disabled = hasChildren ? isRolePermission && allChildrenDisabled : isRolePermission;
        return {
          ...node,
          disabled,
          children
        };
      });
    },
    // 父节点勾选/取消 → 子节点全同步
    handleTreeCheckPC(node, treeStatus) {
      const tree = this.$refs.treeRefPC;
      const clickNode = this.$refs.treeRefPC.getNode(node.id);
      if (!tree) return;

      const isChecked = treeStatus.checkedKeys.includes(node.id);
      if (node.children && node.children.length > 0) {
        this.setChildrenCheck(tree, node.children, isChecked);
      }
      // 核心：有 parentId → 递归向上勾选所有父
      if ((node.parentId !== undefined && node.parentId !== null) || node.isParent === true) {
        if (isChecked) {
          this.setParentNode(clickNode);
        }
      }
    },
    // 递归设置子节点
    setChildrenCheck(tree, children, isChecked) {
      if (!children) return;
      children.forEach((item) => {
        // 방지 disabled state change 避免修改只读角色权限
        if (!item.disabled) {
          tree.setChecked(item, isChecked);
        }
        if (item.isParent === true) {
          this.setChildrenCheck(tree, item.children, isChecked);
        }
      });
    },
    setParentNode(node) {
      if (node.parent && node.parent.key !== undefined) {
        node.parent.checked = true;
        this.setParentNode(node.parent);
      }
    },
    handleTreeCheckPDA(node, treeStatus) {
      const tree = this.$refs.treeRefPDA;
      const clickNode = this.$refs.treeRefPDA.getNode(node.id);
      if (!tree) return;

      const isChecked = treeStatus.checkedKeys.includes(node.id);
      if (node.children && node.children.length > 0) {
        this.setChildrenCheckPDA(tree, node.children, isChecked);
      }
      // 核心：有 parentId → 递归向上勾选所有父
      if ((node.parentId !== undefined && node.parentId !== null) || node.isParent === true) {
        if (isChecked) {
          this.setParentNode(clickNode);
        }
      }
    },
    // 递归设置子节点
    setChildrenCheckPDA(tree, children, isChecked) {
      if (!children) return;
      children.forEach((item) => {
        if (!item.disabled) {
          tree.setChecked(item, isChecked);
        }
        if (item.isParent === true) {
          this.setChildrenCheckPDA(tree, item.children, isChecked);
        }
      });
    },
    async init() {
      const res = await getPermissionStructureAll();
      const {
        permissionStructurePc,
        permissionStructurePda
      } = res.data;

      // 角色权限 ID 集合（用于标记只读节点）
      const rolePermissionPcIds = ((this.permissionTreePop.rolePermissionIds && this.permissionTreePop.rolePermissionIds.pc) || []);
      const rolePermissionPdaIds = ((this.permissionTreePop.rolePermissionIds && this.permissionTreePop.rolePermissionIds.pda) || []);
      const rolePermissionPcIdSet = new Set([...rolePermissionPcIds, ...rolePermissionPcIds.map(String)]);
      const rolePermissionPdaIdSet = new Set([...rolePermissionPdaIds, ...rolePermissionPdaIds.map(String)]);

      // 过滤权限
      const filterPermissionNodes = (nodes, userId) => {
        return nodes.filter((node) => {
          const show = node.code !== 'menu:system-manage:OperationRecord' || userId === 3817;
          if (show && node.children) node.children = filterPermissionNodes(node.children, userId);
          return show;
        });
      };

      const filteredPc = filterPermissionNodes(permissionStructurePc, this.userId);
      this.permissionList.pc = this.markRoleNodes(filteredPc, rolePermissionPcIdSet);
      this.permissionList.pda = this.markRoleNodes(permissionStructurePda, rolePermissionPdaIdSet);

      // 渲染默认勾选
      this.$nextTick(() => {
        this.setDefaultChecked();
      });
    },

    // 设置默认选中（逐个 setChecked 避免 el-tree check-strictly 模式下 setCheckedKeys 的渲染 bug）
    setDefaultChecked() {
      if (!this.permissionTreePop) return;

      const pcTree = this.$refs.treeRefPC;
      const pdaTree = this.$refs.treeRefPDA;

      const pcList = this.permissionTreePop.selectedPermissionList.pc || [];
      const pdaList = this.permissionTreePop.selectedPermissionList.pda || [];

      pcList.forEach((id) => pcTree.setChecked(id, true));
      pdaList.forEach((id) => pdaTree.setChecked(id, true));
    },

    // ✅【修复核心】获取所有选中权限（能正常传给后端）
    getSelectedPermissionList() {
      const treePC = this.$refs.treeRefPC;
      const treePDA = this.$refs.treeRefPDA;

      // check-strictly 模式下，同时获取全选和半选的 key，
      // 确保只勾选父节点（无子节点选中）时父节点 ID 也不会丢失
      const pcChecked = treePC.getCheckedKeys();
      const pcHalf = treePC.getHalfCheckedKeys();
      const pdaChecked = treePDA.getCheckedKeys();
      const pdaHalf = treePDA.getHalfCheckedKeys();

      // 合并去重（用 Set 保证唯一）
      const permissionIds = [...new Set([...pcChecked, ...pcHalf, ...pdaChecked, ...pdaHalf])];

      // 所有选中 + 半选节点
      const allNodes = [
        ...treePC.getCheckedNodes(),
        ...treePC.getHalfCheckedNodes(),
        ...treePDA.getCheckedNodes(),
        ...treePDA.getHalfCheckedNodes()
      ];

      const menuPermissions = [];
      const buttonPermissions = [];

      allNodes.forEach((item) => {
        const prefix = item.code?.split(':')[0];
        if (prefix === 'menu') {
          menuPermissions.push(item.code);
        } else {
          buttonPermissions.push(item.code);
        }
      });

      console.log('最终传给后端的权限ID：', permissionIds);
      return {
        permissionIds,
        menuPermissions,
        buttonPermissions
      };
    }
  }
};
</script>

<style scoped>
.pcpdaClass {
  line-height: 17px;
  font-size: 17px;
  padding: 5px;
  background: linear-gradient(to right, #ededed, #fff);
}

.el-tree {
  min-width: 100%;
  display: inline-block;
}
.tree {
  overflow: auto;
  max-height: 400px;
}

/* 角色权限节点样式：灰色文字，光标 not-allowed */
::v-deep .el-tree-node.is-checked > .el-tree-node__content .el-checkbox.is-disabled .el-checkbox__inner {
  background-color: #c0c4cc;
  border-color: #c0c4cc;
}

::v-deep .el-tree-node__content .el-checkbox.is-disabled {
  cursor: not-allowed;
}

::v-deep .el-tree-node__content .el-checkbox.is-disabled + span {
  color: #909399;
}
</style>
